import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { fmtTime, fmtTimeShort, fmtDate, fmtDateLong } from "@/lib/format";
import { Plane, Car, MapPin, Clock, PlaneTakeoff, PlaneLanding, Info, BedDouble, Moon, Wallet, ShieldCheck, ChevronDown } from "lucide-react";

export default function MyTrip() {
  const { eventId, attendeeId, event, currentPersona, demoMode } = useApp();
  const [trip, setTrip] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [accom, setAccom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.trip(eventId, attendeeId),
      api.context(eventId, attendeeId),
      api.accommodation(eventId, attendeeId).catch(() => null),
    ]).then(([t, c, a]) => {
      if (active) { setTrip(t); setCtx(c); setAccom(a); setLoading(false); }
    });
    return () => { active = false; };
  }, [eventId, attendeeId]);

  const stay = ctx?.your_stay;

  return (
    <div className="pt-16 px-4 pb-6">
      <div className="pt-4 pb-3 flex items-center justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Your journey</span>
          <h1 className="font-serif text-4xl text-cream">My Trip</h1>
        </div>
        {demoMode && <span className="att-gold-chip">Demo information</span>}
      </div>

      {loading ? (
        <div className="h-40 rounded-2xl bg-[hsl(var(--pc-surface))] animate-pulse" />
      ) : (
        <div className="space-y-5">
          {/* FLIGHT */}
          <section>
            <h2 className="font-serif text-2xl text-cream mb-2">Flight</h2>
            {trip?.outbound?.length || trip?.return?.length || trip?.return_self_arranged ? (
              <div className="space-y-3">
                <FlightCard title="Outbound" icon={PlaneTakeoff} flights={trip.outbound} />
                <FlightCard title="Return" icon={PlaneLanding} flights={trip.return} />
                {trip?.return_self_arranged && (
                  <div data-testid="attendee-my-trip-return-self-arranged" className="att-card p-4 flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]"><PlaneLanding className="h-4.5 w-4.5" /></div>
                    <div>
                      <div className="text-sm font-semibold text-cream">Return</div>
                      <div className="text-xs text-cream-dim mt-0.5">Return travel arranged independently</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="attendee-my-trip-flight-card" className="att-card p-5 text-center">
                <Plane className="h-7 w-7 mx-auto text-[hsl(var(--pc-gold))] mb-2" />
                <div className="font-serif text-xl text-cream">Flight details coming soon</div>
                <p className="text-sm text-cream-dim mt-1">{currentPersona?.is_plus_one ? "Linked to your host's itinerary." : "Your flights will appear here once confirmed."}</p>
              </div>
            )}
          </section>

          {/* HOTEL STAY */}
          <section>
            <h2 className="font-serif text-2xl text-cream mb-2">Hotel Stay</h2>
            <div data-testid="attendee-my-trip-hotel-card" className="att-card p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]"><BedDouble className="h-5 w-5" /></div>
                <div><div className="font-semibold text-cream">{event?.host_resort}</div><div className="text-xs text-cream-dim">{event?.destination}</div></div>
              </div>
              {stay?.arrival && (
                <div className="mt-3 flex items-center justify-between text-sm">
                  <div><div className="text-[10px] uppercase text-cream-dim">Check-in</div><div className="text-cream font-semibold">{fmtDateLong(stay.arrival + 'T12:00:00')}</div></div>
                  <div className="flex items-center gap-1 text-cream-dim"><Moon className="h-3.5 w-3.5" /> {stay.nights}n</div>
                  <div className="text-right"><div className="text-[10px] uppercase text-cream-dim">Check-out</div><div className="text-cream font-semibold">{fmtDateLong(stay.departure + 'T12:00:00')}</div><div className="text-[11px] font-medium text-[hsl(var(--pc-gold))]" data-testid="attendee-my-trip-checkout-time">{fmtTime(stay.checkout_time || '11:00')}</div></div>
                </div>
              )}
            </div>
          </section>

          {/* ACCOMMODATION COVERAGE (read-only) */}
          <AccommodationCoverage accom={accom} />

          {/* AIRPORT TRANSFER */}
          <section>
            <h2 className="font-serif text-2xl text-cream mb-2">Airport Transfer</h2>
            {trip?.transfers?.length ? (
              <div className="space-y-3">
                {trip.transfers.map((t) => (
                  <div key={t.id} data-testid="attendee-my-trip-transfer-card" className="att-card p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--pc-gold))]"><Car className="h-3.5 w-3.5" /> {t.direction} · {t.transfer_type}</div>
                    <div className="mt-2 space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-cream"><Clock className="h-4 w-4 text-[hsl(var(--pc-gold))]" /> Pickup {fmtDateLong(t.pickup_datetime)}, {fmtTimeShort(t.pickup_datetime)}</div>
                      <div className="flex items-start gap-2 text-cream"><MapPin className="h-4 w-4 text-[hsl(var(--pc-gold))] mt-0.5" /> {t.pickup_location}</div>
                    </div>
                    <p className="text-xs text-cream-dim mt-2">{t.instructions}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="attendee-my-trip-transfer-card" className="att-card p-5 text-center">
                <Car className="h-7 w-7 mx-auto text-[hsl(var(--pc-gold))] mb-2" />
                <div className="font-serif text-xl text-cream">Transfer details coming soon</div>
                <p className="text-sm text-cream-dim mt-1">A private group transfer from {event?.destination_airport} will be arranged.</p>
              </div>
            )}
          </section>
        </div>
      )}

      <div className="mt-5 flex items-start gap-2 rounded-xl att-glass p-3">
        <Info className="h-4 w-4 mt-0.5 text-[hsl(var(--pc-gold))] shrink-0" />
        <p className="text-xs text-cream-dim">{demoMode
          ? "Travel is populated automatically via a bulk import of flight, rooming and transfer reports — unified here in My Trip. Sample data shown for the prototype."
          : "Your flight and stay details are shown from your confirmed itinerary. Airport transfer details will appear here once arranged."}</p>
      </div>
    </div>
  );
}

function AccommodationCoverage({ accom }) {
  const [open, setOpen] = useState(false);
  if (!accom) return null;

  const status = accom.status;
  const employeeCost = accom.employee_cost || 0;

  // Pending / not-applicable → single calm line, no numbers.
  if (status === "pending" || status === "not_applicable") {
    return (
      <section>
        <h2 className="font-serif text-2xl text-cream mb-2">Accommodation Coverage</h2>
        <div data-testid="attendee-accommodation-card" className="att-card p-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <p className="text-sm text-cream-dim">{accom.headline}</p>
        </div>
      </section>
    );
  }

  const covered = employeeCost === 0;

  return (
    <section>
      <h2 className="font-serif text-2xl text-cream mb-2">Accommodation Coverage</h2>
      <div data-testid="attendee-accommodation-card" className="att-card p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]">
            {covered ? <ShieldCheck className="h-5 w-5" /> : <Wallet className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-cream-dim">{accom.headline}</p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-[hsl(var(--pc-surface2)/0.5)] p-2">
                <div className="text-[10px] uppercase tracking-wide text-cream-dim">Nights</div>
                <div className="font-serif text-xl text-cream" data-testid="attendee-accommodation-total-nights">{accom.total_nights}</div>
              </div>
              <div className="rounded-xl bg-[hsl(var(--pc-surface2)/0.5)] p-2">
                <div className="text-[10px] uppercase tracking-wide text-cream-dim">Collabera-covered</div>
                <div className="font-serif text-xl text-[hsl(var(--pc-gold))]">{accom.covered_nights}</div>
              </div>
              <div className="rounded-xl bg-[hsl(var(--pc-surface2)/0.5)] p-2">
                <div className="text-[10px] uppercase tracking-wide text-cream-dim">Personal</div>
                <div className="font-serif text-xl text-cream">{accom.extension_nights}</div>
              </div>
            </div>

            {!covered && (
              <div className="mt-3 rounded-xl border border-[hsl(var(--pc-gold)/0.3)] bg-[hsl(var(--pc-gold)/0.08)] p-3">
                <div className="text-[11px] uppercase tracking-wide text-cream-dim">Estimated personal accommodation</div>
                <div className="font-serif text-2xl text-cream" data-testid="attendee-accommodation-employee-cost">{accom.employee_cost_display}</div>
                <div className="text-xs text-[hsl(var(--pc-gold))] font-medium">{accom.payment_note}</div>
                <div className="text-[11px] text-cream-dim mt-1">{accom.estimate_note}</div>
              </div>
            )}

            {accom.nights?.length > 0 && (
              <>
                <button
                  onClick={() => setOpen((o) => !o)}
                  data-testid="attendee-accommodation-breakdown-toggle"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--pc-gold))]"
                >
                  {open ? "Hide breakdown" : "View breakdown"}
                  <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div data-testid="attendee-accommodation-breakdown" className="mt-2 divide-y divide-[hsl(var(--pc-line)/0.6)]">
                    {accom.nights.map((n) => (
                      <div key={n.date} className="flex items-center justify-between py-2 text-sm">
                        <div className="text-cream">{fmtDateLong(n.date + "T12:00:00")}</div>
                        <div className="text-right">
                          <div className="text-cream-dim text-xs">{n.label}</div>
                          <div className="text-cream font-medium">{n.amount_display}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FlightCard({ title, icon: Icon, flights }) {
  if (!flights?.length) return null;
  return (
    <>
      {flights.map((f) => (
        <div key={f.id} data-testid="attendee-my-trip-flight-card" className="att-card p-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]"><Icon className="h-4.5 w-4.5" /></div>
            <div>
              <div className="text-sm font-semibold text-cream">{title}{f.airline ? ` · ${f.airline}` : ""}</div>
              {f.flight_number && <div className="text-xs text-cream-dim">{f.flight_number}</div>}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-center"><div className="font-serif text-2xl text-cream">{f.origin_airport || "—"}</div></div>
            <div className="flex-1 px-3">
              <div className="relative h-px bg-[hsl(var(--pc-line))]"><Plane className="h-4 w-4 text-[hsl(var(--pc-gold))] absolute left-1/2 -translate-x-1/2 -top-2" /></div>
              {f.date && <div className="text-center text-[10px] text-cream-dim mt-1">{fmtDate(f.date)}</div>}
            </div>
            <div className="text-center">
              <div className="font-serif text-2xl text-cream">{f.destination_airport || "—"}</div>
              {f.time && <div className="text-xs font-semibold text-[hsl(var(--pc-gold))] mt-1">{f.time_kind === "arrival" ? "Arrives " : "Departs "}{fmtTime(f.time)}</div>}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
